import React, { useMemo, useState } from 'react';

import { Account } from '@ndvr/portfolio-api';

import { SimpleSortableHeader } from '@/components/SimpleSortableHeader';
import { Table, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useSimpleTableSort } from '@/hooks/useSimpleTableSort';
import { getPersonOrgName, useRequiredParams } from 'utils';
import { usePersonOrgProfilesForHousehold } from 'views/person/person-queries';
import { AccountGroupProfileWithAumIncluded, AccountWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useDeleteAccount } from 'views/portfolio/account-queries';
import { DeleteValidationModal } from 'widgets/delete/DeleteValidationModal';

import { AccountRow } from './AccountRow';

type AccountSortColumn = 'name' | 'status' | 'custody' | 'aum' | 'owner';

interface AccountsTableProps {
    accountGroupProfileWithAumIncluded: AccountGroupProfileWithAumIncluded;
    showAccountOwnerColumn?: boolean;
    movedAccountId?: string | null;
    highlightedAccountId?: string | null;
    disableDrag?: boolean;
}

export const AccountsTable = ({
    accountGroupProfileWithAumIncluded,
    showAccountOwnerColumn = true,
    movedAccountId,
    highlightedAccountId,
    disableDrag = false,
}: AccountsTableProps) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutateAsync: deleteAccount, isPending: isDeleteAccountPending } = useDeleteAccount(globalAccountGUID);
    const [accountToDelete, setAccountToDelete] = useState<Account | undefined>();

    // Fetch PersonOrg profiles for the household to enable sorting by owner name
    const { data: personOrgProfiles = [] } = usePersonOrgProfilesForHousehold(globalAccountGUID);

    // Create a map from ownerPersonOrgGUID to owner name for efficient lookups
    const ownerNameMap = useMemo(() => {
        const map = new Map<string, string>();
        personOrgProfiles.forEach((profile) => {
            map.set(profile.personOrgGUID, getPersonOrgName(profile.personOrg));
        });
        return map;
    }, [personOrgProfiles]);

    const handleDeleteAccount = (account: Account) => {
        setAccountToDelete(account);
    };

    // Create sort functions with access to owner names
    const sortFunctions = useMemo<
        Record<AccountSortColumn, (a: AccountWithAumIncluded, b: AccountWithAumIncluded) => number>
    >(() => {
        return {
            name: (a, b) => a.accountName.plainText.localeCompare(b.accountName.plainText),
            status: (a, b) => {
                const statusOrder = {
                    REAL: 1,
                    DRAFT: 2,
                    ONBOARDING: 3,
                    CLOSED: 4,
                } as const;
                return (statusOrder[a.lifecycleStatus] || 999) - (statusOrder[b.lifecycleStatus] || 999);
            },
            custody: (a, b) => {
                const aCustody = a.custodyAccountId || '';
                const bCustody = b.custodyAccountId || '';
                return aCustody.localeCompare(bCustody);
            },
            aum: (a, b) => parseFloat(a.aum || '0') - parseFloat(b.aum || '0'),
            owner: (a, b) => {
                const aOwnerName = a.ownerPersonOrgGUID ? ownerNameMap.get(a.ownerPersonOrgGUID) || '' : '';
                const bOwnerName = b.ownerPersonOrgGUID ? ownerNameMap.get(b.ownerPersonOrgGUID) || '' : '';
                return aOwnerName.localeCompare(bOwnerName);
            },
        };
    }, [ownerNameMap]);

    const {
        sortedData: sortedAccounts,
        sortColumn,
        sortDirection,
        handleSort,
    } = useSimpleTableSort<AccountWithAumIncluded, AccountSortColumn>({
        data: accountGroupProfileWithAumIncluded.accounts,
        sortFunctions,
    });

    return (
        <>
            {accountGroupProfileWithAumIncluded.accounts.length > 0 ? (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent">
                                {!disableDrag && <TableHead className="w-8 py-1"></TableHead>}
                                <SimpleSortableHeader
                                    column="name"
                                    label="ACCOUNT NAME"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    className="min-w-[150px] py-1"
                                />
                                <SimpleSortableHeader
                                    column="status"
                                    label="STATUS"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    className="min-w-[80px] py-1"
                                />
                                <SimpleSortableHeader
                                    column="custody"
                                    label="CUSTODY"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    className="min-w-[120px] py-1"
                                />
                                <SimpleSortableHeader
                                    column="aum"
                                    label="AUM"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    className="min-w-[100px] py-1"
                                />
                                {showAccountOwnerColumn && (
                                    <SimpleSortableHeader
                                        column="owner"
                                        label="ACCOUNT OWNER"
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection}
                                        onSort={handleSort}
                                        className="min-w-[120px] py-1"
                                    />
                                )}
                                <TableHead className="w-8 py-1"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <tbody>
                            {sortedAccounts.map((account) => (
                                <AccountRow
                                    key={account.accountGUID}
                                    account={account}
                                    onDelete={handleDeleteAccount}
                                    showAccountOwnerColumn={showAccountOwnerColumn}
                                    isBeingMoved={movedAccountId === account.accountGUID}
                                    isHighlighted={highlightedAccountId === account.accountGUID}
                                    disableDrag={disableDrag}
                                />
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <div className="flex h-full items-center justify-center">
                    <div className="text-sm text-gray-500">No accounts found</div>
                </div>
            )}
            {accountToDelete && (
                <DeleteValidationModal
                    recordName={accountToDelete.accountName.plainText}
                    disabled={isDeleteAccountPending}
                    onDelete={() => deleteAccount(accountToDelete)}
                    onClose={() => setAccountToDelete(undefined)}
                />
            )}
        </>
    );
};
